import * as React from 'react';
import StyledModal from '../../Components/StyledModal';
import type { UserLetter } from '../../../MedicareProAPI/Entities/User/UserLetter';
import type { GlobUserTemplate } from '../../../MedicareProAPI/Entities/Glob/GlobUserTemplate';
import { UserLetterRepository } from '../../../MedicareProAPI/Repositories/User/UserLetterRepository';
import { GlobUserTemplateRepository } from '../../../MedicareProAPI/Repositories/Glob/GlobUserTemplateRepository';
import type { TemplateType } from '../../Constants/AgentReportingTemplates';
import { TEMPLATE_TYPES } from '../../Constants/AgentReportingTemplates';

interface TemplateSelectorModalProps {
  userId: number;
  userAccessToken: string;
  open: boolean;
  onClose: () => void;
  onTemplateSelect: (type: TemplateType, payload?: any) => void;
  uplineId: number;
  absoluteUplineId: number;
}

/**
 * Modal component for selecting starter templates (blank, starter, shared)
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-05-20
 * @lastUpdated
 * @version 1.1.0
 */
export const TemplateSelectorModal = ({
  userId,
  userAccessToken,
  open,
  onClose,
  onTemplateSelect,
  uplineId,
  absoluteUplineId,
}: TemplateSelectorModalProps): React.JSX.Element => {
  const userLetterRepository = new UserLetterRepository();
  const globUserTemplateRepository = new GlobUserTemplateRepository();

  const [starterTemplateSelected, setStarterTemplateSelected] = React.useState<GlobUserTemplate>();
  const [agencyTemplateSelected, setAgencyTemplateSelected] = React.useState<UserLetter>();
  const [agencyTemplates, setAgencyTemplates] = React.useState<Array<UserLetter>>([]);
  const [globUserTemplates, setGlobUserTemplates] = React.useState<Array<GlobUserTemplate>>([]);
  const [typeSelected, setTypeSelected] = React.useState<TemplateType | null>(null);
  const [selectedCategories, setSelectedCategories] = React.useState<string[]>([]);
  const [uniqueCategories, setUniqueCategories] = React.useState<string[]>([]);

  userLetterRepository.setAccessToken(userAccessToken);
  globUserTemplateRepository.setAccessToken(userAccessToken);

  /**
   * Sets the selected starter template and clears the selected agency template.
   *
   * @param template - The GlobUserTemplate to set as the selected starter template
   */
  const handleStarterTemplateSelected = (template: GlobUserTemplate): void => {
    setStarterTemplateSelected(template);
    setAgencyTemplateSelected(null);
  };

  /**
   * Sets the selected shared template (as an agency template) and clears the starter template.
   *
   * @param template - The UserLetter to set as the selected shared template
   */
  const handleSharedTemplateSelected = (template: UserLetter): void => {
    setStarterTemplateSelected(null);
    setAgencyTemplateSelected(template);
  };

  /**
   * Fetches and sets global, upline, and absolute upline templates for the user.
   * - Retrieves global templates via `globUserTemplateRepository`
   * - If `uplineId` is present, fetches sharable templates from that upline
   * - If `absoluteUplineId` is defined and differs from `uplineId`, fetches those as well
   */
  const fetchData = async (): Promise<void> => {
    const mergedTemplates: UserLetter[] = [];

    const globUserTemplates = await globUserTemplateRepository.getGlobUserTemplates();
    setGlobUserTemplates(globUserTemplates.getItems());

    if (uplineId) {
      const uplineTemplates = await userLetterRepository.getUserLettersSharable(userId, uplineId);
      mergedTemplates.push(...uplineTemplates.getItems());
    }

    if (absoluteUplineId && absoluteUplineId !== uplineId) {
      const absoluteUplineTemplates = await userLetterRepository.getUserLettersSharable(
        userId,
        absoluteUplineId
      );
      mergedTemplates.push(...absoluteUplineTemplates.getItems());
    }

    setAgencyTemplates(mergedTemplates);
  };

  /**
   * Get new list of categories based on type of template (shared or starter)
   */
  React.useEffect(() => {
    let uniqueCategories = [];

    if (typeSelected === TEMPLATE_TYPES.STARTER) {
      uniqueCategories = Array.from(
        new Set(globUserTemplates.map((template) => template.getCategory()).filter(Boolean))
      );
    }

    if (typeSelected == TEMPLATE_TYPES.SHARED) {
      uniqueCategories = Array.from(
        new Set(agencyTemplates.map((template) => template.getFolder()).filter(Boolean))
      );
    }

    setUniqueCategories(uniqueCategories);
  }, [typeSelected]);

  React.useEffect(() => {
    const run = async () => {
      await fetchData();
    };

    run();
  }, [userId, userAccessToken]);

  return (
    <StyledModal
      size="xl"
      title="Template Builder"
      footerCloseButton={true}
      show={open}
      onClose={onClose}
    >
      <div className="row">
        <div className="col-12">
          <div className="row">
            {/* Blank Template */}
            <div className="col">
              <div
                className="card border cp pt-3 pb-2 border-secondary mb-2 templateStartOption"
                data-type="blank"
                style={{ height: 'auto' }}
                onClick={() => onTemplateSelect(TEMPLATE_TYPES.BLANK)}
              >
                <div className="card-body text-center">
                  <h5>
                    <i className="fas fa-file text-secondary"></i>
                  </h5>
                  <div>
                    <b>Blank Template</b>
                  </div>
                  <p style={{ fontSize: '9pt' }}>
                    <i>Start from scratch and design your own email or letter template</i>
                  </p>
                </div>
              </div>
            </div>

            {/* Starter Template */}
            <div className="col">
              <div
                className="card border cp pt-3 pb-2 border-info mb-2 templateStartOption"
                data-type="starter"
                style={{ height: 'auto' }}
                onClick={() => setTypeSelected(TEMPLATE_TYPES.STARTER)}
              >
                <div className="card-body text-center">
                  <h5>
                    <i className="fas fa-file-alt text-info"></i>
                  </h5>
                  <div>
                    <b>Starter Template</b>
                  </div>
                  <p style={{ fontSize: '9pt' }}>
                    <i>Start with a pre-made template and edit to your liking.</i>
                  </p>
                </div>
              </div>
            </div>

            {/* Shared Template (conditional) */}
            {(uplineId || absoluteUplineId) && (
              <div className="col">
                <div
                  className="card border cp pt-3 pb-2 border-primary mb-2 templateStartOption"
                  data-type="shared"
                  style={{ height: 'auto' }}
                  onClick={() => setTypeSelected(TEMPLATE_TYPES.SHARED)}
                >
                  <div className="card-body text-center">
                    <h5>
                      <i className="fas fa-file-user text-primary"></i>
                    </h5>
                    <div>
                      <b>Shared Template</b>
                    </div>
                    <p style={{ fontSize: '9pt' }}>
                      <i>Start with a template that has been shared by your upline.</i>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Side Panels */}
        <div className="col-4 templateOptionsReturn">
          {typeSelected && <h6 className="font-weight-bold mb-2 mt-2">Templates</h6>}
          {typeSelected && (
            <div className="mb-3">
              {uniqueCategories.map((category) => {
                const isSelected = selectedCategories.includes(category);
                return (
                  <span
                    key={category}
                    className={`badge badge-pill mr-2 cp ${isSelected ? 'badge-primary' : 'badge-light'}`}
                    style={{ fontSize: '8pt' }}
                    onClick={() => {
                      setSelectedCategories((prev) =>
                        isSelected ? prev.filter((c) => c !== category) : [...prev, category]
                      );
                    }}
                  >
                    {category}
                  </span>
                );
              })}
            </div>
          )}
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {typeSelected === TEMPLATE_TYPES.STARTER &&
              globUserTemplates
                .filter(
                  (template) =>
                    selectedCategories.length === 0 ||
                    selectedCategories.includes(template.getCategory())
                )
                .map((template, templateIndex) => (
                  <div
                    className="rounded mb-2 p-2 border border-info cp"
                    key={templateIndex}
                    onClick={() => handleStarterTemplateSelected(template)}
                  >
                    <div>
                      <i className="far fa-file-alt text-primary mr-2"></i>
                      {template.getTemplateTitle()}
                    </div>
                    <div className="badge badge-info" style={{ fontSize: '8pt' }}>
                      {template.getTemplateType()}
                    </div>
                    <div
                      className="badge badge-pill table-success text-dark data-badge ml-2"
                      style={{ fontSize: '8pt' }}
                    >
                      {template.getCategory()}
                    </div>
                  </div>
                ))}

            {typeSelected === TEMPLATE_TYPES.SHARED && (
              <>
                {agencyTemplates.length > 0 &&
                  agencyTemplates
                    .filter(
                      (template) =>
                        selectedCategories.length === 0 ||
                        selectedCategories.includes(template.getFolder())
                    )
                    .map((template, templateIndex) => (
                      <div
                        className="rounded mb-2 p-2 border border-info cp"
                        key={`agency-${templateIndex}`}
                        onClick={() => handleSharedTemplateSelected(template)}
                      >
                        <div>
                          <i className="far fa-file-alt text-primary mr-2"></i>
                          {template.getLetterTitle()}
                        </div>
                        <div className="badge badge-info" style={{ fontSize: '8pt' }}>
                          {template.getLetterType()}
                        </div>
                        <div
                          className="badge badge-pill table-success data-badge ml-2"
                          style={{ fontSize: '8pt' }}
                        >
                          {template.getFolder()}
                        </div>
                      </div>
                    ))}
              </>
            )}
          </div>
        </div>
        <div className="col-8 border-left templateChoserDisplay">
          {typeSelected === TEMPLATE_TYPES.STARTER && starterTemplateSelected && (
            <>
              <div className="mb-2 font-weight-bold mt-2">
                {starterTemplateSelected.getTemplateTitle()}
                <div className="badge badge-info ml-2" style={{ fontSize: '8pt' }}>
                  {starterTemplateSelected.getTemplateType()}
                </div>
              </div>
              <div
                className="mb-2 btn btn-sm border-primary templateChosen starterTemplate"
                onClick={() => onTemplateSelect(typeSelected, starterTemplateSelected)}
              >
                <i className="fas fa-check-circle mr-2 text-primary"></i>Choose this template
              </div>
              <div className="templateChoserContent border border-secondary rounded p-2">
                <div
                  dangerouslySetInnerHTML={{ __html: starterTemplateSelected.getTemplateBody() }}
                />
              </div>
            </>
          )}
          {typeSelected === TEMPLATE_TYPES.SHARED && agencyTemplateSelected && (
            <>
              <div className="mb-2 font-weight-bold mt-2">
                {agencyTemplateSelected.getLetterTitle()}
                <div className="badge badge-info ml-2" style={{ fontSize: '8pt' }}>
                  {agencyTemplateSelected.getLetterType()}
                </div>
              </div>
              <div
                className="mb-2 btn btn-sm border-primary templateChosen starterTemplate"
                onClick={() => onTemplateSelect(typeSelected, agencyTemplateSelected)}
              >
                <i className="fas fa-check-circle mr-2 text-primary"></i>Choose this template
              </div>
              <div className="templateChoserContent border border-secondary rounded p-2">
                <div dangerouslySetInnerHTML={{ __html: agencyTemplateSelected.getLetterBody() }} />
              </div>
            </>
          )}
        </div>
      </div>
    </StyledModal>
  );
};
